import cx from "classnames";
import { RedesignTextArea, SCREEN_WIDTH_ENUM } from "component-library";
import { useTranslation } from "i18n";
import { useDynamicWindowWidthFor, useTailwindTheme } from "shared-hooks";
import { Theme } from "shared-types";
import ExpandableSanitizedHtml from "pages/Profile/components/common/ExpandableSanitizedHtml";
import EditButton from "pages/Profile/components/common/EditButton";
import EditSectionActions from "pages/Profile/components/ProfileHeader/components/EditSectionActions";
import {
  HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT,
  MAX_HIGHLIGHTS_CHAR_LENGTH,
} from "pages/Profile/components/ProfileHeader/constants";
import { dataTestIds } from "data-testids";

interface HighlightsSectionProps {
  highlightsText: string;
  hasHighlights: boolean;
  editingHighlights: boolean;
  draftHighlights: string;
  highlightsError: string | null;
  highlightsSaveDisabled: boolean;
  isEdit?: boolean;
  isPublicView: boolean;
  alignWithVideoColumn?: boolean;
  onOpenEdit: () => void;
  onCloseEdit: () => void;
  onSave: () => void;
  onDraftChange: (value: string) => void;
}

const HighlightsSection = ({
  highlightsText,
  hasHighlights,
  editingHighlights,
  draftHighlights,
  highlightsError,
  highlightsSaveDisabled,
  isEdit,
  isPublicView,
  alignWithVideoColumn = false,
  onOpenEdit,
  onCloseEdit,
  onSave,
  onDraftChange,
}: HighlightsSectionProps) => {
  const { t } = useTranslation();
  const [theme] = useTailwindTheme();
  const isDark = theme === Theme.Dark;
  const isSm = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.SM);
  const isMd = useDynamicWindowWidthFor(SCREEN_WIDTH_ENUM.MD);

  const showMoreCharThreshold = isSm
    ? HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT.sm
    : isMd
      ? HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT.md
      : HIGHLIGHTS_SHOW_MORE_CHAR_THRESHOLD_BY_BREAKPOINT.lg;

  if (!editingHighlights) {
    return (
      <div
        className={cx(
          "mt-0 flex w-full max-w-full items-center gap-3 text-sm md:flex",
          !alignWithVideoColumn && "md:max-w-[75%]",
          "isolate col-span-2 row-start-3 min-w-0 md:col-auto md:row-auto"
        )}
        data-testid={dataTestIds.profile["Profile.Header.aboutMeSection"]}
      >
        {hasHighlights ? (
          <div className="min-w-0 flex-1">
            <ExpandableSanitizedHtml
              html={highlightsText}
              limit={showMoreCharThreshold}
              contentClassName="[overflow-wrap:anywhere] text-s leading-[1.4219rem] text-grey-void dark:text-white/70"
            />
          </div>
        ) : (
          isEdit &&
          !isPublicView && (
            <span className="text-s italic leading-[1.25rem] text-color-neutral-two dark:text-white/70">
              {t("performer:profile.header.addHighlight")}
            </span>
          )
        )}
        {isEdit && !isPublicView && (
          <EditButton
            onClick={onOpenEdit}
            className="shrink-0 text-black dark:text-white"
          />
        )}
      </div>
    );
  }

  return (
    <div
      className={cx(
        "profile-header-edit-section flex w-full max-w-full flex-col rounded-[0.625rem] p-0",
        !alignWithVideoColumn && "md:max-w-[75%]",
        "font-tertiary text-s font-normal leading-[1.4219rem] text-color-neutral-one dark:text-color-neutral-two",
        { "profile-header-edit-section--dark": isDark },
        !isDark && "text-grey-void",
        "col-span-2 row-start-3 min-w-0 md:col-auto md:row-auto"
      )}
    >
      <RedesignTextArea
        placeholder={t("performer:profile.header.highlightsPlaceholder")}
        value={draftHighlights}
        onValueChange={onDraftChange}
        maxLength={MAX_HIGHLIGHTS_CHAR_LENGTH}
        warningThreshold={1900}
        showCharacterCount
        alwaysShowCharacterCount
        resizable
        error={Boolean(highlightsError)}
        containerClassName="profile-header-edit-section__highlights-textarea"
        data-testid={dataTestIds.profile["Profile.AboutMeEdit.textArea"]}
      />
      <EditSectionActions
        onSave={onSave}
        onCancel={onCloseEdit}
        saveDisabled={highlightsSaveDisabled}
        saveLabel={t("common:button.label.save")}
        cancelLabel={t("common:button.label.cancel")}
      />
    </div>
  );
};

export default HighlightsSection;
